package or.garden.manager.features.fertilizer

import androidx.compose.runtime.*
import karika.distribucija.ba.domain.model.ResultState
import or.garden.manager.domain.NaturalFertilizerService
import or.garden.manager.domain.model.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.disabled

@Composable
fun CreateFertilizerScreen(
    fertilizerId: Int? = null,
    onBack: () -> Unit,
    onSuccess: () -> Unit
) {
    val userRole = or.garden.manager.domain.GardenHttpClient.role
    val isAdmin = userRole == "ADMIN"

    var name by remember { mutableStateOf("") }
    var type by remember { mutableStateOf(FertilizerType.FERMENTATION) }
    var applicationMethod by remember { mutableStateOf(ApplicationMethod.BOTH) }
    var ingredients by remember { mutableStateOf("") }
    var dilutionRatio by remember { mutableStateOf("") }
    var notes by remember { mutableStateOf("") }
    var status by remember { mutableStateOf(FertilizerStatus.PREPARING) }
    var isOrganic by remember { mutableStateOf(true) }

    var isSaving by remember { mutableStateOf(false) }
    var isGeneratingAi by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(fertilizerId != null) }
    var errorMessage by remember { mutableStateOf<String?>(null) } // Error state

    LaunchedEffect(fertilizerId) {
        if (fertilizerId != null) {
            NaturalFertilizerService.getAll { state ->
                if (state is ResultState.Success) {
                    val existing = state.data.data?.find { it.id == fertilizerId }
                    if (existing != null) {
                        name = existing.name
                        type = existing.type
                        applicationMethod = existing.applicationMethod
                        ingredients = existing.ingredients ?: ""
                        dilutionRatio = existing.dilutionRatio ?: ""
                        notes = existing.notes ?: ""
                        status = existing.status
                        isOrganic = existing.isOrganic
                    }
                }
                isLoading = false
            }
        }
    }

    Div({ classes("max-w-4xl", "mx-auto", "pb-20") }) {
        // Top Bar
        Div({ classes("flex", "items-center", "justify-between", "mb-8") }) {
            Button({
                classes("flex", "items-center", "gap-2", "text-gray-500", "hover:text-emerald-600", "transition-colors", "font-bold")
                onClick { onBack() }
            }) {
                I({ classes("fas", "fa-arrow-left") })
                Text("Nazad")
            }
            H2({ classes("text-2xl", "font-bold", "text-gray-900") }) {
                Text(if (fertilizerId == null) "Novo đubrivo" else "Izmjeni đubrivo")
            }
            Div({ classes("w-20") }) // Spacer
        }

        if (isLoading) {
            Div({ classes("flex", "justify-center", "py-20") }) {
                I({ classes("fas", "fa-circle-notch", "fa-spin", "text-4xl", "text-emerald-500") })
            }
        } else {
            errorMessage?.let { msg ->
                Div({ 
                    classes("mx-8", "mt-8", "p-4", "bg-red-50", "border", "border-red-200", "rounded-2xl", "text-red-700", "text-sm")
                }) {
                    Text(msg)
                }
            }

            Div({ classes("bg-white", "rounded-3xl", "shadow-sm", "border", "border-gray-100", "overflow-hidden") }) {
                // AI Section - Only for Admins
                if (fertilizerId == null && isAdmin) {
                    Div({ classes("p-8", "bg-emerald-50/50", "border-b", "border-emerald-100") }) {
                        Label(attrs = { classes("block", "text-sm", "font-bold", "text-emerald-800", "mb-3") }) {
                            I({ classes("fas", "fa-magic", "mr-2") })
                            Text("AI Generator - Automatsko popunjavanje")
                        }
                        Div({ classes("flex", "gap-3") }) {
                            Input(InputType.Text) {
                                classes("flex-1", "px-4", "py-3", "bg-white", "border", "border-emerald-200", "rounded-2xl", "outline-none", "focus:ring-2", "focus:ring-emerald-500")
                                placeholder("npr. đubrivo za folijarno zaljevanje od gaveza")
                                id("ai-prompt-input")
                            }
                            Button(attrs = {
                                classes("px-6", "py-3", "bg-emerald-600", "text-white", "rounded-2xl", "font-bold", "hover:bg-emerald-700", "transition-all")
                                if (isGeneratingAi) {
                                    classes("opacity-50")
                                    disabled()
                                }
                                onClick {
                                    val promptInput = kotlinx.browser.document.getElementById("ai-prompt-input") as? org.w3c.dom.HTMLInputElement
                                    val promptValue = promptInput?.value ?: ""
                                    if (promptValue.isNotBlank()) {
                                        isGeneratingAi = true
                                        errorMessage = null
                                        NaturalFertilizerService.generateAi(promptValue) { state ->
                                            when (state) {
                                                is ResultState.Success -> {
                                                    val data = state.data.data
                                                    if (data != null) {
                                                        name = data.name
                                                        type = data.type
                                                        applicationMethod = data.applicationMethod
                                                        ingredients = data.ingredients
                                                        dilutionRatio = data.dilutionRatio
                                                        notes = data.notes
                                                        isOrganic = data.isOrganic
                                                    }
                                                }
                                                is ResultState.Error -> {
                                                    errorMessage = state.message
                                                }
                                                else -> {}
                                            }
                                            isGeneratingAi = false
                                        }
                                    }
                                }
                            }) {
                                if (isGeneratingAi) I({ classes("fas", "fa-circle-notch", "fa-spin", "mr-2") })
                                Text(if (isGeneratingAi) "Generisanje..." else "Generiši")
                            }
                        }
                    }
                }

                Div({ classes("p-8", "space-y-8") }) {
                    // Form Grid
                    Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-8") }) {
                        // Left Column
                        Div({ classes("space-y-6") }) {
                            Div {
                                Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Naziv đubriva") }
                                Input(InputType.Text) {
                                    classes("w-full", "px-4", "py-3", "bg-gray-50", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                                    value(name)
                                    onInput { name = it.value }
                                    placeholder("npr. Kopriva, Gavez...")
                                }
                            }

                            Div {
                                Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-3") }) { Text("Tip preparata") }
                                Div({ classes("grid", "grid-cols-1", "gap-3") }) {
                                    FertilizerType.entries.forEach { t ->
                                        val isSelected = type == t
                                        Div({
                                            classes("p-4", "rounded-xl", "border-2", "cursor-pointer", "flex", "items-center", "justify-between", "transition-all")
                                            if (isSelected) classes("border-emerald-500", "bg-emerald-50", "text-emerald-700") else classes("border-gray-100", "hover:border-emerald-200")
                                            onClick { type = t }
                                        }) {
                                            Div {
                                                Div({ classes("font-bold") }) {
                                                    Text(when(t) {
                                                        FertilizerType.FERMENTATION -> "Fermentacija"
                                                        FertilizerType.TEA -> "Biljni čaj"
                                                        FertilizerType.CHEMICAL_REACTION -> "Reakcija"
                                                    })
                                                }
                                                Div({ classes("text-xs", "opacity-70") }) {
                                                    Text(when(t) {
                                                        FertilizerType.FERMENTATION -> "Proces traje obično 14 dana"
                                                        FertilizerType.TEA -> "Spremno nakon 24h natapanja"
                                                        FertilizerType.CHEMICAL_REACTION -> "Spremno odmah nakon miješanja"
                                                    })
                                                }
                                            }
                                            if (isSelected) I({ classes("fas", "fa-check-circle") })
                                        }
                                    }
                                }
                            }
                        }

                        // Right Column
                        Div({ classes("space-y-6") }) {
                            Div {
                                Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-3") }) { Text("Način primjene") }
                                Div({ classes("flex", "gap-3") }) {
                                    ApplicationMethod.entries.forEach { m ->
                                        val isSelected = applicationMethod == m
                                        Div({
                                            classes("flex-1", "py-3", "rounded-xl", "border-2", "cursor-pointer", "text-center", "font-bold", "text-sm", "transition-all")
                                            if (isSelected) classes("border-emerald-500", "bg-emerald-50", "text-emerald-700") else classes("border-gray-100", "hover:border-emerald-100")
                                            onClick { applicationMethod = m }
                                        }) {
                                            Text(when(m) {
                                                ApplicationMethod.FOLIAR -> "Folijarno"
                                                ApplicationMethod.ROOT -> "Korijen"
                                                ApplicationMethod.BOTH -> "Oboje"
                                            })
                                        }
                                    }
                                }
                            }

                            Div {
                                Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Sastojci") }
                                TextArea {
                                    classes("w-full", "px-4", "py-3", "bg-gray-50", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none", "h-32")
                                    value(ingredients)
                                    onInput { ingredients = it.value }
                                    placeholder("Navedite sastojke i količine...")
                                }
                            }

                            Div {
                                Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Omjer razblaživanja") }
                                Input(InputType.Text) {
                                    classes("w-full", "px-4", "py-3", "bg-gray-50", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none")
                                    value(dilutionRatio)
                                    onInput { dilutionRatio = it.value }
                                    placeholder("npr. 1:10")
                                }
                            }
                        }
                    }

                    // Bottom Row
                    Div({ classes("grid", "grid-cols-1", "md:grid-cols-2", "gap-8") }) {
                        Div {
                            Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-2") }) { Text("Napomene") }
                            TextArea {
                                classes("w-full", "px-4", "py-3", "bg-gray-50", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none", "h-24")
                                value(notes)
                                onInput { notes = it.value }
                                placeholder("Dodatne informacije o primjeni ili skladištenju...")
                            }
                        }

                        Div({ classes("space-y-6") }) {
                            if (fertilizerId != null) {
                                Div {
                                    Label(attrs = { classes("block", "text-sm", "font-bold", "text-gray-700", "mb-3") }) { Text("Status") }
                                    Div({ classes("flex", "gap-2", "flex-wrap") }) {
                                        FertilizerStatus.entries.forEach { s ->
                                            val isSelected = status == s
                                            Div({
                                                classes("px-4", "py-2", "rounded-lg", "border", "cursor-pointer", "text-xs", "font-bold", "transition-all")
                                                if (isSelected) classes("border-emerald-600", "bg-emerald-600", "text-white") else classes("border-gray-200", "text-gray-500", "hover:bg-gray-50")
                                                onClick { status = s }
                                            }) {
                                                Text(when(s) {
                                                    FertilizerStatus.PREPARING -> "U pripremi"
                                                    FertilizerStatus.READY -> "Spremno"
                                                    FertilizerStatus.EXPIRED -> "Isteklo"
                                                    FertilizerStatus.ARCHIVED -> "Arhivirano"
                                                })
                                            }
                                        }
                                    }
                                }
                            }

                            Div({ classes("flex", "items-center", "gap-3", "p-4", "bg-gray-50", "rounded-xl") }) {
                                Input(InputType.Checkbox) {
                                    classes("w-5", "h-5", "accent-emerald-600")
                                    id("is-organic-checkbox")
                                    checked(isOrganic)
                                    onInput { isOrganic = it.value }
                                }
                                Label(forId = "is-organic-checkbox", attrs = { classes("text-sm", "font-bold", "text-gray-700", "cursor-pointer") }) {
                                    Text("Ovo je organsko đubrivo")
                                }
                            }
                        }
                    }

                    // Action Buttons
                    Div({ classes("flex", "justify-end", "gap-4", "pt-6", "border-t", "border-gray-100") }) {
                        Button({
                            classes("px-8", "py-3", "bg-white", "text-gray-600", "border", "border-gray-200", "rounded-2xl", "font-bold", "hover:bg-gray-50", "transition-all")
                            onClick { onBack() }
                        }) { Text("Otkaži") }

                        Button({
                            val btnClasses = mutableListOf("px-10", "py-3", "bg-emerald-600", "text-white", "rounded-2xl", "font-bold", "shadow-lg", "hover:bg-emerald-700", "transition-all", "flex", "items-center", "gap-2")
                            if (name.isBlank() || isSaving) {
                                btnClasses.add("opacity-50")
                                btnClasses.add("cursor-not-allowed")
                            }
                            classes(btnClasses)
                            if (name.isNotBlank() && !isSaving) {
                                onClick {
                                    isSaving = true
                                    if (fertilizerId == null) {
                                        val req = CreateNaturalFertilizerRequest(
                                            name = name,
                                            type = type,
                                            ingredients = ingredients,
                                            applicationMethod = applicationMethod,
                                            dilutionRatio = dilutionRatio,
                                            notes = notes,
                                            isOrganic = isOrganic
                                        )
                                        NaturalFertilizerService.create(req) { state ->
                                            if (state is ResultState.Success) onSuccess()
                                            isSaving = false
                                        }
                                    } else {
                                        val req = UpdateNaturalFertilizerRequest(
                                            name = name,
                                            type = type,
                                            status = status,
                                            ingredients = ingredients,
                                            applicationMethod = applicationMethod,
                                            dilutionRatio = dilutionRatio,
                                            notes = notes,
                                            isOrganic = isOrganic
                                        )
                                        NaturalFertilizerService.update(fertilizerId, req) { state ->
                                            if (state is ResultState.Success) onSuccess()
                                            isSaving = false
                                        }
                                    }
                                }
                            }
                        }) {
                            if (isSaving) I({ classes("fas", "fa-circle-notch", "fa-spin") })
                            Text(if (fertilizerId == null) "Kreiraj đubrivo" else "Sačuvaj izmjene")
                        }
                    }
                }
            }
        }
    }
}
